import { useState } from "react";
import { useT } from "../i18n";
import { templateFor } from "../vault/catalog";
import { TypeIcon } from "./icons";
import type { ItemFilter } from "../vault/items";
import { getSavedSearches, addSavedSearch, removeSavedSearch, type SavedSearch } from "../vault/prefs";

// Faceted filter bar (UX phase 3): type + tag (multi, OR within a facet) and a modified-date window
// (single). Combines with the free-text query (held by the parent). Saved searches persist the whole
// ItemFilter per user. Designed to sit on the entries toolbar; renders nothing heavy until opened.
const DATE_OPTIONS: { label: string; days: number | null }[] = [
  { label: "Any time", days: null },
  { label: "Last 24 hours", days: 1 },
  { label: "Last 7 days", days: 7 },
  { label: "Last 30 days", days: 30 },
];

export function SearchFilters({
  filter,
  onChange,
  types,
  tags,
}: {
  filter: ItemFilter;
  onChange: (f: ItemFilter) => void;
  types: { type: string; count: number }[];
  tags: string[];
}) {
  const t = useT();
  const [saved, setSaved] = useState<SavedSearch[]>(() => getSavedSearches());

  const toggle = (key: "types" | "tags", value: string) => {
    const cur = filter[key];
    const next = cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value];
    onChange({ ...filter, [key]: next });
  };

  function saveCurrent() {
    const name = window.prompt(t("Name this search"));
    if (!name?.trim()) return;
    setSaved(addSavedSearch(name.trim(), filter));
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5 text-xs">
      <FacetMenu label={t("Type")} count={filter.types.length}>
        {types.length === 0 && <Empty>{t("No items")}</Empty>}
        {types.map(({ type, count }) => (
          <CheckRow key={type} checked={filter.types.includes(type)} onChange={() => toggle("types", type)}>
            <span className="text-muted"><TypeIcon type={type} className="h-3.5 w-3.5" /></span>
            <span className="flex-1">{templateFor(type).label}</span>
            <span className="font-mono text-muted">{count}</span>
          </CheckRow>
        ))}
      </FacetMenu>

      <FacetMenu label={t("Tags")} count={filter.tags.length}>
        {tags.length === 0 && <Empty>{t("No tags")}</Empty>}
        {tags.map((tag) => (
          <CheckRow key={tag} checked={filter.tags.includes(tag)} onChange={() => toggle("tags", tag)}>
            <span className="flex-1 truncate">{tag}</span>
          </CheckRow>
        ))}
      </FacetMenu>

      <select
        value={filter.modifiedWithinDays ?? ""}
        onChange={(e) => onChange({ ...filter, modifiedWithinDays: e.target.value === "" ? null : Number(e.target.value) })}
        title={t("Modified")}
        className={`rounded border px-2 py-0.5 ${filter.modifiedWithinDays != null ? "border-accent text-fg" : "border-border text-muted"} bg-panel`}
      >
        {DATE_OPTIONS.map((d) => (
          <option key={d.label} value={d.days ?? ""}>
            {d.days == null ? t("Modified") + ": " + t("Any time") : t(d.label)}
          </option>
        ))}
      </select>

      <FacetMenu label={t("Saved")} count={0}>
        {saved.length === 0 && <Empty>{t("No saved searches")}</Empty>}
        {saved.map((s) => (
          <div key={s.id} className="flex items-center gap-1">
            <button onClick={() => onChange({ ...s.filter })} className="flex-1 truncate rounded px-1.5 py-1 text-left hover:bg-panel2">
              {s.name}
            </button>
            <button onClick={() => setSaved(removeSavedSearch(s.id))} title={t("Delete")} className="px-1 text-muted hover:text-danger">
              ×
            </button>
          </div>
        ))}
        <div className="my-1 border-t border-border" />
        <button onClick={saveCurrent} className="w-full rounded px-1.5 py-1 text-left text-accent hover:bg-panel2">
          + {t("Save current search")}
        </button>
      </FacetMenu>

      {(filter.types.length > 0 || filter.tags.length > 0 || filter.modifiedWithinDays != null) && (
        <button
          onClick={() => onChange({ ...filter, types: [], tags: [], modifiedWithinDays: null })}
          className="rounded border border-border px-2 py-0.5 text-muted hover:border-accent hover:text-fg"
        >
          {t("Clear filters")}
        </button>
      )}
    </div>
  );
}

// A button that toggles a checkbox popover. Closes on outside click via a fixed backdrop.
function FacetMenu({ label, count, children }: { label: string; count: number; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className={`rounded border px-2 py-0.5 ${count > 0 ? "border-accent text-fg" : "border-border text-muted hover:border-accent"}`}
      >
        {label}
        {count > 0 ? ` · ${count}` : ""} ▾
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute z-20 mt-1 max-h-72 w-56 overflow-auto rounded-md border border-border bg-panel p-1 shadow-xl">
            {children}
          </div>
        </>
      )}
    </div>
  );
}

function CheckRow({ checked, onChange, children }: { checked: boolean; onChange: () => void; children: React.ReactNode }) {
  return (
    <label className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 hover:bg-panel2">
      <input type="checkbox" checked={checked} onChange={onChange} className="h-3 w-3 shrink-0" />
      {children}
    </label>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div className="px-1.5 py-2 text-center text-muted">{children}</div>;
}
